// The neighbour-notification bus (ARCH §27.9, SCR-14).
//
// ENG-13 resolves connection state; it does not NOTIFY. This is the bus that does:
// every block edit tells its 6 face neighbours that something next to them changed,
// so `on neighbor` (M46.2) can fire without every block polling its surroundings.
//
// **Notifications are queued, never synchronous.** `World.setBlockState` enqueues and
// returns; nothing dispatches inside the mutation. Firing synchronously would re-enter
// the edit path mid-mutation — a handler could observe a half-applied world, and one
// edit could mint an unbounded cascade *within a single tick* with no budget to stop
// it. Queuing hands the cascade to the tick pipeline, where it is bounded by the same
// discipline RED-1 already runs on: the queue drains once, in the next `scheduledTicks`
// phase, and anything past the cap is dropped and COUNTED rather than silently lost.
//
// The queue is deliberately SEPARATE from `Chunk.scheduledTicks` (D-TF-205): that array
// is persisted and feeds `TickHooks.onScheduledTick`, so notifications riding in it
// would both bloat every save and deliver 6 phantom scheduled ticks per edit to the
// redstone/fluid consumers — which is exactly the RED-7 byte-identity §27.9 makes
// binding. A parallel queue drained in the same phase keeps every property §27.9 states
// (queued, not synchronous; drains next phase; bounded) and keeps RED-7 untouched.
import { NOTIFY } from '../config';

/** The 6 face neighbours, in the order a notification batch is enqueued.
 *
 *  **This order is part of the determinism contract.** Dispatch order decides which
 *  handlers run first and therefore which are still inside the cascade budget when it
 *  clips, so a reordering here would clip a different set on a different machine. It is
 *  -Y first (the block you stand on is the one that most often matters), then +Y, then
 *  the four sides in -Z/+Z/-X/+X order. */
export const NEIGHBOR_OFFSETS: ReadonlyArray<readonly [number, number, number]> = [
  [0, -1, 0],
  [0, 1, 0],
  [0, 0, -1],
  [0, 0, 1],
  [-1, 0, 0],
  [1, 0, 0],
] as const;

/** How many notifications one edit enqueues — the 6 faces. Named so the tests and the
 *  budget arithmetic quote the contract rather than a bare 6. */
export const NEIGHBORS_PER_EDIT = NEIGHBOR_OFFSETS.length;

/** One queued "your neighbour changed" message.
 *
 *  It carries the SOURCE cell as well as the target because `on neighbor`'s event view
 *  (§27.9) reports which side the change came from — a handler that cannot tell a
 *  change below it from one beside it cannot implement a falling block or a torch. */
export interface Notification {
  /** The cell being notified. */
  readonly x: number;
  readonly y: number;
  readonly z: number;
  /** The cell that changed — always a face neighbour of (x, y, z). */
  readonly fromX: number;
  readonly fromY: number;
  readonly fromZ: number;
  /** Cascade depth (§27.10). A player edit enqueues at 0; a notification dispatched at
   *  depth N that edits a block enqueues the consequences at N+1, which is what stops a
   *  chain of reacting blocks from running forever inside one tick. */
  readonly depth: number;
}

/** The bounded FIFO a world keeps for one tick's worth of neighbour notifications.
 *
 *  Bounded by `NOTIFY.MAX_QUEUE` rather than trusted to stay small: a script `fill` of
 *  `SCR.FILL_MAX` cells mints 24 576 notifications in one call, and a flowing ocean
 *  edits thousands of cells a tick through `setFluidBlock`. The cap is the backstop that
 *  keeps the queue O(cap) instead of O(edits); `dropped` is what makes the clip visible
 *  to the author instead of a mystery (E-46). */
export class NotifyQueue {
  private queue: Notification[] = [];
  private droppedCount = 0;

  /** Notifications waiting to be drained. */
  get size(): number {
    return this.queue.length;
  }

  /** Notifications dropped by the cap since the last `resetDropped`. Surfaced in the
   *  editor's diagnostics (§27.16) — a cascade being clipped must be visible. */
  get dropped(): number {
    return this.droppedCount;
  }

  /** Enqueue the 6 face-neighbour notifications for an edit at (x, y, z).
   *
   *  Returns how many were actually enqueued — fewer than `NEIGHBORS_PER_EDIT` when the
   *  cap bit, so the caller can report a partial batch. The batch is NOT atomic: taking
   *  the first few and dropping the rest keeps the -Y-first order meaningful under
   *  pressure, where refusing the whole batch would make a full queue silently skip
   *  whole edits. */
  enqueueNeighbors(x: number, y: number, z: number, depth = 0): number {
    let enqueued = 0;
    for (const [dx, dy, dz] of NEIGHBOR_OFFSETS) {
      if (this.queue.length >= NOTIFY.MAX_QUEUE) {
        this.droppedCount += NEIGHBORS_PER_EDIT - enqueued;
        return enqueued;
      }
      this.queue.push({ x: x + dx, y: y + dy, z: z + dz, fromX: x, fromY: y, fromZ: z, depth });
      enqueued++;
    }
    return enqueued;
  }

  /** Take everything queued and clear the queue.
   *
   *  Called once per tick from the `scheduledTicks` phase. It returns a snapshot rather
   *  than draining lazily so that notifications enqueued BY this batch's handlers land
   *  in the NEXT tick's batch — the property that makes a cascade advance one depth per
   *  tick instead of spinning inside one. */
  drain(): Notification[] {
    if (this.queue.length === 0) return [];
    const out = this.queue;
    this.queue = [];
    return out;
  }

  /** Zero the drop counter — the editor does this when the author clears diagnostics. */
  resetDropped(): void {
    this.droppedCount = 0;
  }

  /** Discard everything pending. Used when a world unloads, so a stale cell coordinate
   *  from a dropped chunk can never be dispatched against a world that no longer has it. */
  clear(): void {
    this.queue = [];
    this.droppedCount = 0;
  }
}
